import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageSquare, 
  Search, 
  RefreshCw,
  Calendar,
  Star,
  Mail,
  FileText,
  Send
} from 'lucide-react';
import MediCureOnSidebar from '../common/MediCureOnSidebar';
import { apiConfig } from '../../config/apiConfig';

const FeedbackAdmin = ({ onNavigate }) => {
  // State management
  const [feedbackList, setFeedbackList] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    assignedTo: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [responseText, setResponseText] = useState('');
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);

  // Load data on component mount
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Load feedback list
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const feedbackResponse = await fetch(`${apiConfig.backendUrl}${apiConfig.endpoints.feedback.admin.list}?${queryParams}&code=${apiConfig.functionKeys.feedbackAdmin}`);
      const feedbackData = await feedbackResponse.json();
      setFeedbackList(feedbackData);

      // Load analytics
      const analyticsResponse = await fetch(`${apiConfig.backendUrl}${apiConfig.endpoints.feedback.admin.analytics}?code=${apiConfig.functionKeys.feedbackAdmin}`);
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleStatusUpdate = async (feedbackId, updates) => {
    try {
      const response = await fetch(`${apiConfig.backendUrl}${apiConfig.endpoints.feedback.admin.update(feedbackId)}?code=${apiConfig.functionKeys.feedbackAdmin}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        loadDashboardData(); // Refresh data
        if (selectedFeedback && selectedFeedback.id === feedbackId) {
          const updatedFeedback = await response.json();
          setSelectedFeedback(updatedFeedback);
        }
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };

  const handleAddResponse = async (feedbackId) => {
    if (!responseText.trim()) return;

    setIsSubmittingResponse(true);
    try {
      const response = await fetch(`${apiConfig.backendUrl}${apiConfig.endpoints.feedback.admin.response(feedbackId)}?code=${apiConfig.functionKeys.feedbackAdmin}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responderId: 'admin@medicureon.com', // TODO: Get from auth
          responderName: 'Admin',
          message: responseText,
          isPublic: true
        })
      });

      if (response.ok) {
        const updatedFeedback = await response.json();
        setSelectedFeedback(updatedFeedback);
        setResponseText('');
        loadDashboardData(); // Refresh list
      }
    } catch (error) {
      console.error('Error adding response:', error);
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  if (loading && !feedbackList.data) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <MediCureOnSidebar onNavigate={onNavigate} activePage="feedback-admin" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading feedback dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <MediCureOnSidebar onNavigate={onNavigate} activePage="feedback-admin" />

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Feedback List */}
        <div className="flex flex-col w-1/2 bg-white border-r border-gray-200">
          {/* Header */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200" style={{ backgroundColor: '#02276F' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/10 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">Feedback Management</h1>
                  <p className="text-sm text-blue-100">
                    {analytics?.summary.total || 0} total submissions
                  </p>
                </div>
              </div>
              <button
                onClick={loadDashboardData}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <RefreshCw className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Analytics Overview */}
          {analytics && (
            <div className="flex-shrink-0 p-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{analytics.summary.new}</div>
                  <div className="text-xs text-gray-600">New</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{analytics.summary.inProgress}</div>
                  <div className="text-xs text-gray-600">In Progress</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{analytics.summary.resolved}</div>
                  <div className="text-xs text-gray-600">Resolved</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">{analytics.summary.urgent}</div>
                  <div className="text-xs text-gray-600">Urgent</div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200">
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search feedback..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          {/* Feedback List */}
          <div className="flex-1 overflow-y-auto">
            {feedbackList.data?.map((feedback) => (
              <div
                key={feedback.id}
                onClick={() => setSelectedFeedback(feedback)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                  selectedFeedback?.id === feedback.id ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 truncate">{feedback.title}</h3>
                    <p className="text-sm text-gray-600 truncate">{feedback.description}</p>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(feedback.priority)}`}>
                      {feedback.priority}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(feedback.status)}`}>
                      {feedback.status}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-2">
                    <span>{feedback.category}</span>
                    {feedback.rating > 0 && (
                      <div className="flex items-center space-x-1">
                        {renderStars(feedback.rating)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {feedback.responses?.length > 0 && (
                      <span className="flex items-center">
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {feedback.responses.length}
                      </span>
                    )}
                    <span>{formatDate(feedback.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {feedbackList.pagination && (
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Page {feedbackList.pagination.currentPage} of {feedbackList.pagination.totalPages}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!feedbackList.pagination.hasPrev}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!feedbackList.pagination.hasNext}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Feedback Detail */}
        <div className="flex-1 flex flex-col">
          {selectedFeedback ? (
            <>
              {/* Detail Header */}
              <div className="flex-shrink-0 p-6 border-b border-gray-200 bg-white">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedFeedback.priority)}`}>
                        {selectedFeedback.priority.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">{selectedFeedback.category} • {selectedFeedback.type}</span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedFeedback.title}</h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(selectedFeedback.createdAt)}
                      </span>
                      {selectedFeedback.submittedBy?.email && (
                        <span className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {selectedFeedback.submittedBy.email}
                        </span>
                      )}
                      {selectedFeedback.rating > 0 && (
                        <div className="flex items-center space-x-1">
                          {renderStars(selectedFeedback.rating)}
                          <span>({selectedFeedback.rating}/5)</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedFeedback.status}
                      onChange={(e) => handleStatusUpdate(selectedFeedback.id, { status: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="new">New</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    
                    <select
                      value={selectedFeedback.assignedTo || ''}
                      onChange={(e) => handleStatusUpdate(selectedFeedback.id, { assignedTo: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Unassigned</option>
                      <option value="admin@medicureon.com">Admin</option>
                      <option value="support@medicureon.com">Support</option>
                      <option value="dev@medicureon.com">Development</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Detail Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedFeedback.description}</p>
                  </div>
                </div>

                {/* Attachments */}
                {selectedFeedback.attachments?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Attachments</h3>
                    <div className="space-y-2">
                      {selectedFeedback.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <FileText className="w-5 h-5 text-gray-500" />
                          <span className="flex-1">{attachment.name}</span>
                          <span className="text-sm text-gray-500">
                            {(attachment.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Responses */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Responses ({selectedFeedback.responses?.length || 0})
                  </h3>
                  
                  <div className="space-y-4">
                    {selectedFeedback.responses?.map((response) => (
                      <div key={response.id} className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{response.responderName}</span>
                          <span className="text-sm text-gray-500">{formatDate(response.timestamp)}</span>
                        </div>
                        <p className="text-gray-700">{response.message}</p>
                      </div>
                    ))}
                  </div>

                  {/* Add Response */}
                  <div className="mt-4">
                    <div className="flex space-x-3">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Add a response..."
                        rows={3}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => handleAddResponse(selectedFeedback.id)}
                        disabled={!responseText.trim() || isSubmittingResponse}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {isSubmittingResponse ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        <span>Send</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Select a feedback item to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackAdmin;